import { useDebounce } from '@vueuse/core';
import { useExperienceApi } from '@/shared/api/useExperienceApi';
import { TOTAL_COUNT_REQUEST_DEBOUNCE } from '../../const/config';
import { excursionFiltersToQuery } from '@/entities/filter/store/excursion';
import type { TExcursionFiltersPageState, TExcursionFiltersState } from '../../types/excursion/state';
import { useUnlicensedContentFilter } from '@/shared/composables/useUnlicensedContentFilter.ts';
import { useDisplayCurrencyQueryParams } from '@/shared/composables/useDisplayCurrencyQueryParams.ts';
import { useDraftDisplayCurrencyQueryParams } from '@/shared/composables/useDisplayedCurrency.ts';
import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';

interface UseExcursionFiltersTotalCountOptions {
  useDraftDisplayCurrency?: boolean;
}

export function useExcursionFiltersTotalCount(
  state: TExcursionFiltersState,
  pageParams: TExcursionFiltersPageState,
  options: UseExcursionFiltersTotalCountOptions = {},
) {
  const { getExperiencesCount } = useExperienceApi();
  const { hideUnlicensed } = useUnlicensedContentFilter();
  const displayCurrencyQueryParams = options.useDraftDisplayCurrency
    ? useDraftDisplayCurrencyQueryParams()
    : useDisplayCurrencyQueryParams();

  const finalQueryParams = computed(() => {
    const queryParams = excursionFiltersToQuery(state, pageParams);

    return {
      ...queryParams,
      ...displayCurrencyQueryParams.value,
      exclude_needs_license: hideUnlicensed.value,
      date_filter_mode: 'event_start',
      extended_movement_types: true,
    };
  });

  const debouncedQueryParams = useDebounce(finalQueryParams, TOTAL_COUNT_REQUEST_DEBOUNCE);

  const queryKey = computed(() => {
    return ['experiencesTotalCount', JSON.stringify(debouncedQueryParams.value)];
  });

  const {
    data: totalCount,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: () => {
      return getExperiencesCount({ query: debouncedQueryParams.value });
    },
    placeholderData: keepPreviousData,
    enabled: computed(() => Boolean(debouncedQueryParams.value.city__slug)),
    select: (response) => response.data?.count ?? 0,
    staleTime: 1000 * 60 * 5, // 5 минут
  });

  return {
    isLoading: computed(() => isLoading.value || isFetching.value),
    totalCount,
  };
}
